import { apiRequest, downloadFile } from './client';
import type {
  Access,
  DeliveryView,
  Category,
  CopyResult,
  EtaItemCode,
  InvoiceKind,
  MockScenario,
  NoteBasis,
  NoteSources,
  OrgActivityCode,
  OrgCurrency,
  ActivityCode,
  AuditEntry,
  Branch,
  BranchInput,
  Discount,
  Draft,
  DraftSaved,
  IssuedResult,
  Item,
  ItemDetail,
  ItemInput,
  Locale,
  Me,
  Organization,
  OrganizationTax,
  Page,
  Party,
  PartyDetail,
  PartyInput,
  Preview,
  SignedResult,
  SubmissionRequest,
  SupersedeResult,
  RefCode,
  ReceiptDetail,
  ReceiptDraftPayload,
  ReceiptIssued,
  ReceiptPreview,
  PosDevice,
  ReceiptSaved,
  ReceiptSummary,
  SalesDocumentDetail,
  StateChangeRequestView,
  SalesDocumentSummary,
  Session,
  TaxType,
  UnitType,
  EtaEnvironment,
  DocumentStatus,
  BulkResponse,
  BulkTask,
  EtaStatusDetail,
} from './types';

/** Typed wrappers around every endpoint the web app uses (one place to read the contract). */

const org = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;

export interface PageQuery {
  page?: number;
  limit?: number;
}

// ----------------------------------------------------------------------------- auth
export const authApi = {
  me: (signal?: AbortSignal) =>
    apiRequest<Me>('/auth/me', { ignoreUnauthenticated: true, ...(signal ? { signal } : {}) }),
  login: (input: { email: string; password: string }) =>
    apiRequest<Session>('/auth/login', {
      method: 'POST',
      body: input,
      ignoreUnauthenticated: true,
    }),
  register: (input: { email: string; password: string; displayName: string; locale: Locale }) =>
    apiRequest<Session>('/auth/register', {
      method: 'POST',
      body: input,
      ignoreUnauthenticated: true,
    }),
  logout: () =>
    apiRequest<undefined>('/auth/logout', { method: 'POST', ignoreUnauthenticated: true }),
};

// ----------------------------------------------------------------------------- organizations
export interface CreateOrganizationInput {
  name: string;
  legalName?: string;
  rin?: string;
  etaEnvironment: EtaEnvironment;
  baseCurrency: string;
}

export const orgApi = {
  create: (input: CreateOrganizationInput) =>
    apiRequest<{ id: string }>('/orgs', { method: 'POST', body: input }),
  get: (orgId: string, signal?: AbortSignal) =>
    apiRequest<Organization>(org(orgId), signal ? { signal } : {}),
  update: (
    orgId: string,
    input: {
      version: number;
      name?: string;
      legalName?: string | null;
      rin?: string | null;
      email?: string | null;
      phone?: string | null;
    },
  ) => apiRequest<{ id: string; version: number }>(org(orgId), { method: 'PATCH', body: input }),
  access: (orgId: string, signal?: AbortSignal) =>
    apiRequest<Access>(`${org(orgId)}/access`, signal ? { signal } : {}),
  branches: (orgId: string, signal?: AbortSignal) =>
    apiRequest<Branch[]>(`${org(orgId)}/branches`, signal ? { signal } : {}),
  createBranch: (orgId: string, input: BranchInput) =>
    apiRequest<Branch>(`${org(orgId)}/branches`, { method: 'POST', body: input }),
  taxes: (orgId: string, signal?: AbortSignal) =>
    apiRequest<OrganizationTax[]>(`${org(orgId)}/taxes`, signal ? { signal } : {}),
  createTax: (
    orgId: string,
    input: { taxType: string; taxSubtype: string; rate?: string; nameAr: string; nameEn: string },
  ) => apiRequest<{ id: string }>(`${org(orgId)}/taxes`, { method: 'POST', body: input }),
  setActivityCodes: (orgId: string, input: { codes: string[]; primary: string }) =>
    apiRequest<{ codes: string[]; primary: string }>(`${org(orgId)}/activity-codes`, {
      method: 'PUT',
      body: input,
    }),
  activityCodes: (orgId: string, signal?: AbortSignal) =>
    apiRequest<OrgActivityCode[]>(`${org(orgId)}/activity-codes`, signal ? { signal } : {}),
  currencies: (orgId: string, signal?: AbortSignal) =>
    apiRequest<OrgCurrency[]>(`${org(orgId)}/currencies`, signal ? { signal } : {}),
  addCurrency: (orgId: string, code: string) =>
    apiRequest<{ code: string }>(`${org(orgId)}/currencies`, { method: 'POST', body: { code } }),
  categories: (orgId: string, scope: 'item' | 'party', signal?: AbortSignal) =>
    apiRequest<Category[]>(`${org(orgId)}/categories`, {
      query: { scope },
      ...(signal ? { signal } : {}),
    }),
  createCategory: (
    orgId: string,
    input: { scope: 'item' | 'party'; nameAr: string; nameEn?: string },
  ) => apiRequest<Category>(`${org(orgId)}/categories`, { method: 'POST', body: input }),
  etaItemCodes: (orgId: string, q: string, signal?: AbortSignal) =>
    apiRequest<EtaItemCode[]>(`${org(orgId)}/eta-item-codes`, {
      query: { q },
      ...(signal ? { signal } : {}),
    }),
};

// ----------------------------------------------------------------------------- reference data
export const refApi = {
  unitTypes: (q: string, signal?: AbortSignal) =>
    apiRequest<UnitType[]>('/ref/unit-types', {
      query: { q, limit: 50 },
      ...(signal ? { signal } : {}),
    }),
  activityCodes: (q: string, signal?: AbortSignal) =>
    apiRequest<ActivityCode[]>('/ref/activity-codes', {
      query: { q, limit: 50 },
      ...(signal ? { signal } : {}),
    }),
  taxTypes: (signal?: AbortSignal) =>
    apiRequest<TaxType[]>('/ref/tax-types', signal ? { signal } : {}),
  currencies: (signal?: AbortSignal) =>
    apiRequest<RefCode[]>('/ref/currencies', signal ? { signal } : {}),
  countries: (signal?: AbortSignal) =>
    apiRequest<RefCode[]>('/ref/countries', signal ? { signal } : {}),
  weightUnitTypes: (signal?: AbortSignal) =>
    apiRequest<RefCode[]>('/ref/weight-unit-types', signal ? { signal } : {}),
  /** ETA payment-method codes (receipts). */
  paymentMethods: (signal?: AbortSignal) =>
    apiRequest<RefCode[]>('/ref/payment-methods', signal ? { signal } : {}),
};

// ----------------------------------------------------------------------------- parties
export interface PartyListQuery extends PageQuery {
  role?: 'customer' | 'supplier';
  q?: string;
  archived?: boolean;
}

export const partyApi = {
  list: (orgId: string, query: PartyListQuery, signal?: AbortSignal) =>
    apiRequest<Page<Party>>(`${org(orgId)}/parties`, {
      query: { ...query },
      ...(signal ? { signal } : {}),
    }),
  get: (orgId: string, id: string, signal?: AbortSignal) =>
    apiRequest<PartyDetail>(`${org(orgId)}/parties/${encodeURIComponent(id)}`, {
      ...(signal ? { signal } : {}),
    }),
  create: (orgId: string, input: PartyInput) =>
    apiRequest<{ id: string; version: number }>(`${org(orgId)}/parties`, {
      method: 'POST',
      body: input,
    }),
  update: (orgId: string, id: string, input: Partial<PartyInput> & { version: number }) =>
    apiRequest<{ id: string; version: number }>(`${org(orgId)}/parties/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),
};

// ----------------------------------------------------------------------------- items
export interface ItemListQuery extends PageQuery {
  q?: string;
}

export const itemApi = {
  list: (orgId: string, query: ItemListQuery, signal?: AbortSignal) =>
    apiRequest<Page<Item>>(`${org(orgId)}/items`, {
      query: { ...query },
      ...(signal ? { signal } : {}),
    }),
  get: (orgId: string, id: string, signal?: AbortSignal) =>
    apiRequest<ItemDetail>(`${org(orgId)}/items/${encodeURIComponent(id)}`, {
      ...(signal ? { signal } : {}),
    }),
  create: (orgId: string, input: ItemInput) =>
    apiRequest<{ id: string; version: number }>(`${org(orgId)}/items`, {
      method: 'POST',
      body: input,
    }),
  update: (
    orgId: string,
    id: string,
    input: {
      version: number;
      nameAr?: string;
      nameEn?: string | null;
      description?: string | null;
      unitPrice?: string;
      unitCode?: string;
      categoryId?: string | null;
      defaultDiscount?: Discount | null;
      defaultItemsDiscount?: string | null;
      defaultValueDifference?: string | null;
    },
  ) =>
    apiRequest<{ id: string; version: number }>(`${org(orgId)}/items/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),
};

// ----------------------------------------------------------------------------- sales documents
/** ETA document types: invoice, credit note, debit note, and their export variants. */
export type DocumentKind = 'i' | 'c' | 'd' | 'ei' | 'ec' | 'ed';
export type DocumentTab = 'invoices' | 'credit' | 'debit';
export type SalesSort = 'number' | 'date' | 'customer' | 'amount' | 'status' | 'updated';

export interface SalesListQuery extends PageQuery {
  q?: string;
  tab?: DocumentTab;
  /** WP-1b.3: exactly these documents (comma-separated ids, at most 100). */
  ids?: string;
  /** WP-1b.3: only documents a bulk task applies to (the bulk route's own predicate). */
  eligibleFor?: BulkTask;
  /** Comma-separated lists (the API accepts several values). */
  status?: string;
  taxStatus?: string;
  receiverPartyId?: string;
  from?: string;
  to?: string;
  minAmount?: string;
  maxAmount?: string;
  sort?: SalesSort;
  dir?: 'asc' | 'desc';
}

const doc = (orgId: string, id: string) =>
  `${org(orgId)}/sales/documents/${encodeURIComponent(id)}`;

export const salesApi = {
  list: (orgId: string, query: SalesListQuery, signal?: AbortSignal) =>
    apiRequest<Page<SalesDocumentSummary>>(`${org(orgId)}/sales/documents`, {
      query: { ...query },
      ...(signal ? { signal } : {}),
    }),
  get: (orgId: string, id: string, signal?: AbortSignal) =>
    apiRequest<SalesDocumentDetail>(doc(orgId, id), { ...(signal ? { signal } : {}) }),
  nextNumber: (orgId: string, kind: InvoiceKind, signal?: AbortSignal) =>
    apiRequest<{ number: string | null }>(`${org(orgId)}/sales/numbering/next`, {
      query: { kind },
      ...(signal ? { signal } : {}),
    }),
  create: (orgId: string, draft: Draft, kind: InvoiceKind = 'i') =>
    apiRequest<DraftSaved>(`${org(orgId)}/sales/documents`, {
      method: 'POST',
      body: { kind, draft },
    }),
  update: (orgId: string, id: string, version: number, draft: Draft, kind?: InvoiceKind) =>
    apiRequest<DraftSaved>(doc(orgId, id), {
      method: 'PATCH',
      body: { version, draft, ...(kind ? { kind } : {}) },
    }),
  preview: (orgId: string, kind: InvoiceKind, draft: Draft, signal?: AbortSignal) =>
    apiRequest<Preview>(`${org(orgId)}/sales/documents/preview`, {
      method: 'POST',
      body: { kind, draft },
      ...(signal ? { signal } : {}),
    }),
  /** Issue AND send to ETA, in one transaction (OD-3). */
  /** Issue and send; `emailBuyer` only from the editor's step 4 (WP-1b.4). */
  issue: (orgId: string, id: string, version: number, emailBuyer = false) =>
    apiRequest<IssuedResult>(`${doc(orgId, id)}/issue`, {
      method: 'POST',
      body: { version, ...(emailBuyer ? { emailBuyer: true } : {}) },
    }),
  /** WP-1b.4: a buyer copy whose hand-over failed goes back to the queue (same recipient). */
  retryEmail: (orgId: string, id: string) =>
    apiRequest<{ delivery: DeliveryView | null }>(`${doc(orgId, id)}/email/retry`, {
      method: 'POST',
    }),
  /** E-seal as a separate step (WP-1b.1): sign an issued, unsigned document; no ETA call. */
  sign: (orgId: string, id: string) =>
    apiRequest<SignedResult>(`${doc(orgId, id)}/sign`, { method: 'POST' }),
  /** Send a signed (or issued) document to ETA; the worker reuses the stored signature verbatim. */
  submit: (orgId: string, id: string) =>
    apiRequest<SubmissionRequest>(`${doc(orgId, id)}/submit`, { method: 'POST' }),
  discard: (orgId: string, id: string, version: number) =>
    apiRequest<{ id: string; version: number; status: DocumentStatus; number: string | null }>(
      `${doc(orgId, id)}/discard`,
      { method: 'POST', body: { version } },
    ),
  copy: (orgId: string, id: string) =>
    apiRequest<CopyResult>(`${doc(orgId, id)}/copy`, { method: 'POST' }),
  /** Correct and resend (WP-1b.2): the draft that supersedes a refused document (idempotent). */
  supersede: (orgId: string, id: string) =>
    apiRequest<SupersedeResult>(`${doc(orgId, id)}/supersede`, { method: 'POST' }),
  /** Downloads the PDF of the document as it is now (current ETA status). */
  downloadPdf: (orgId: string, id: string, fallbackName: string) =>
    downloadFile(`${doc(orgId, id)}/pdf`, fallbackName),
  /**
   * Block 11 (D-2): ask the Tax Authority to cancel the document. 202 with the queued request, 200
   * with the same request when one is already in flight. `etaStateSeq` is the marker of the ETA
   * state the page showed (N-03).
   */
  cancelAtEta: (orgId: string, id: string, reason: string, etaStateSeq: string) =>
    apiRequest<StateChangeRequestView>(`${doc(orgId, id)}/eta/cancel`, {
      method: 'POST',
      body: { reason, etaStateSeq },
    }),
  /** Block 11 (F-03): object to the buyer's rejection at the Tax Authority. */
  declineRejection: (orgId: string, id: string, etaStateSeq: string) =>
    apiRequest<StateChangeRequestView>(`${doc(orgId, id)}/eta/decline-rejection`, {
      method: 'POST',
      body: { etaStateSeq },
    }),
  /** Block 11 (D-4): «تحقق الآن من المصلحة» — the worker makes one reading (202). */
  checkAtEta: (orgId: string, id: string) =>
    apiRequest<{ readRequested: boolean }>(`${doc(orgId, id)}/eta/check`, { method: 'POST' }),
  /** Block 11 (R2-01): release a cancellation the Tax Authority took but never confirmed. */
  releaseRequest: (orgId: string, id: string, requestId: string) =>
    apiRequest<{ requestId: string; releasePending: boolean; repeated: boolean }>(
      `${doc(orgId, id)}/eta/requests/${encodeURIComponent(requestId)}/release`,
      { method: 'POST' },
    ),
  /** The document's ETA state, submissions and the Tax Authority's verdict rows. */
  eta: (orgId: string, id: string, signal?: AbortSignal) =>
    apiRequest<EtaStatusDetail>(`${doc(orgId, id)}/eta`, { ...(signal ? { signal } : {}) }),
  /**
   * Bulk task on 1–100 documents (WP-1b.3): one result per id, each under the single action's
   * rules; `dryRun` evaluates and changes nothing (the review).
   */
  bulk: (
    orgId: string,
    body: {
      action: BulkTask;
      ids: string[];
      versions?: Record<string, number>;
      dryRun?: boolean;
    },
    signal?: AbortSignal,
  ) =>
    apiRequest<BulkResponse>(`${org(orgId)}/sales/documents/bulk`, {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    }),
  /** MOCK ETA only: how the mock will answer for this document (refused in production). */
  setMockScenario: (orgId: string, id: string, scenario: MockScenario) =>
    apiRequest<{ documentId: string; scenario: MockScenario }>(`${doc(orgId, id)}/mock-scenario`, {
      method: 'PUT',
      body: { scenario },
    }),
  /**
   * Block 11 (D-3): the valid invoices a note of `kind` may reference, by number, buyer name or tax
   * ID, optionally of one buyer; each with what a credit note may still reduce (the remainder).
   */
  noteSources: (
    orgId: string,
    params: { kind: InvoiceKind; q?: string; receiverPartyId?: string; limit?: number },
    signal?: AbortSignal,
  ) =>
    apiRequest<NoteSources>(`${org(orgId)}/sales/note-sources`, {
      query: {
        kind: params.kind,
        ...(params.q ? { q: params.q } : {}),
        ...(params.receiverPartyId ? { receiverPartyId: params.receiverPartyId } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
      },
      ...(signal ? { signal } : {}),
    }),
  /** Block 11 (D-9, D-11): one invoice's lines and credit cap, for a note of `kind` built on it. */
  noteBasis: (orgId: string, id: string, kind: InvoiceKind, signal?: AbortSignal) =>
    apiRequest<NoteBasis>(`${doc(orgId, id)}/note-basis`, {
      query: { kind },
      ...(signal ? { signal } : {}),
    }),
};

// ----------------------------------------------------------------------------- e-receipts
export type ReceiptSort = 'number' | 'date' | 'buyer' | 'amount' | 'status' | 'updated';

export interface ReceiptListQuery extends PageQuery {
  q?: string;
  /** Comma-separated lists (the API accepts several values). */
  status?: string;
  etaStatus?: string;
  receiptType?: string;
  branchId?: string;
  posDeviceId?: string;
  from?: string;
  to?: string;
  minAmount?: string;
  maxAmount?: string;
  /** Required with minAmount / maxAmount (WP-8.9c). */
  currency?: string;
  /** The receipt with this uuid (a return's reference). */
  uuid?: string;
  sort?: ReceiptSort;
  dir?: 'asc' | 'desc';
}

const receipt = (orgId: string, id: string) => `${org(orgId)}/receipts/${encodeURIComponent(id)}`;

export const receiptsApi = {
  list: (orgId: string, query: ReceiptListQuery, signal?: AbortSignal) =>
    apiRequest<Page<ReceiptSummary>>(`${org(orgId)}/receipts`, {
      query: { ...query },
      ...(signal ? { signal } : {}),
    }),
  get: (orgId: string, id: string, signal?: AbortSignal) =>
    apiRequest<ReceiptDetail>(receipt(orgId, id), { ...(signal ? { signal } : {}) }),
  /** The number the next saved draft on this POS will receive (display only). */
  nextNumber: (orgId: string, posDeviceId: string, signal?: AbortSignal) =>
    apiRequest<{ number: string | null }>(`${org(orgId)}/receipts/numbering/next`, {
      query: { posDeviceId },
      ...(signal ? { signal } : {}),
    }),
  create: (orgId: string, draft: ReceiptDraftPayload) =>
    apiRequest<ReceiptSaved>(`${org(orgId)}/receipts`, { method: 'POST', body: { draft } }),
  update: (orgId: string, id: string, version: number, draft: ReceiptDraftPayload) =>
    apiRequest<ReceiptSaved>(receipt(orgId, id), {
      method: 'PATCH',
      body: { version, draft },
    }),
  /** Issue AND send to ETA, in one transaction (OD-3). */
  issue: (orgId: string, id: string, version: number) =>
    apiRequest<ReceiptIssued>(`${receipt(orgId, id)}/issue`, {
      method: 'POST',
      body: { version },
    }),
  /** The amounts of an unsaved draft, computed as the issue computes them (writes nothing). */
  preview: (orgId: string, draft: ReceiptDraftPayload, signal?: AbortSignal) =>
    apiRequest<ReceiptPreview>(`${org(orgId)}/receipts/preview`, {
      method: 'POST',
      body: { draft },
      ...(signal ? { signal } : {}),
    }),
  /** The organization's in-service POS devices (the editor's picker). */
  devices: (orgId: string, signal?: AbortSignal) =>
    apiRequest<PosDevice[]>(`${org(orgId)}/pos-devices`, { ...(signal ? { signal } : {}) }),
};

// ----------------------------------------------------------------------------- audit
export interface AuditQuery extends PageQuery {
  entityType?: string;
  entityId?: string;
  action?: string;
}

export const auditApi = {
  list: (orgId: string, query: AuditQuery, signal?: AbortSignal) =>
    apiRequest<Page<AuditEntry>>(`${org(orgId)}/audit`, {
      query: { ...query },
      ...(signal ? { signal } : {}),
    }),
};
